import { useState } from "react";
import { Form } from "react-final-form";
import { Box, Collapse, Stack, Typography } from "@mui/material";
import { SubmitButton } from "../common/components/SubmitButton";
import { useRecaptchaReady } from "../common/hooks/useRecaptchaReady";
import arrayMutators from "final-form-arrays";
import { ChildrenFields, DEFAULT_CHILD_FIELDS } from "./ChildrenFields";
import { CfiHeader } from "./CfiHeader";
import { ParentFields } from "./ParentFields";
import { CfiChild, CfiFormResult } from "./cfiTypes";
import { TextFormField } from "../common/components/FormFields";
import { ItemFieldsList } from "./ItemFieldsList/ItemFieldsList";
import { LettersOfSupport } from "./LettersOfSupport";
import { submitCfiApplication } from "../common/api/cfiApplicationApi";
import { ErrorNotification } from "../common/components/ErrorNotification";
import { useNavigate } from "react-router-dom";
import { HouseholdFields } from "./HouseholdFields";
import { Collection } from "./Collection";
import { ConsentAndAuthorization } from "./ConsentAndAuthorization";
import dayjs from "dayjs";
import { CfiDocumentGate } from "./CfiDocumentGate";
import { validateCfiForm } from "./validation";
import { focusFirstInvalidField } from "../common/focusFirstInvalidField";

const RECAPTCHA_ACTION = "SubmitCfiApplication";

//Declare this outside so that it's the same object being passed to the
// initial values each time instead of recreating it on each render
const initialValues: Partial<CfiFormResult> = {
  children: [{ ...(DEFAULT_CHILD_FIELDS as CfiChild) }],
  parents: {
    contactNumber: "",
    email: "",
    address: { country: "Canada" },
  },
  dateSigned: dayjs().startOf("day").toISOString(),
};

export interface CfiFormProps {}

export function CfiForm({}: CfiFormProps) {
  const recaptchaReady = useRecaptchaReady();
  const navigate = useNavigate();
  const [showError, setShowError] = useState(false);

  const [showForm, setShowForm] = useState(false);

  const submit = async (result: CfiFormResult) => {
    try {
      const clientKey = process.env.REACT_APP_RECAPTCHA_CLIENT_KEY ?? "";
      const token = await grecaptcha.enterprise.execute(clientKey, {
        action: RECAPTCHA_ACTION,
      });

      await submitCfiApplication(result, token, RECAPTCHA_ACTION);

      navigate("/success");
    } catch {
      setShowError(true);
    }
  };

  return (
    <>
      <Stack gap={2}>
        <CfiHeader />
        <Box mb={2}>
          <CfiDocumentGate onApply={() => setShowForm(true)} />
        </Box>
        <Collapse in={showForm} unmountOnExit>
          <Form<CfiFormResult>
            onSubmit={submit}
            subscription={{ submitting: true }}
            mutators={{ ...arrayMutators }}
            initialValues={initialValues}
            validate={validateCfiForm}
            render={({ handleSubmit, submitting }) => (
              <form
                onSubmitCapture={focusFirstInvalidField}
                onSubmit={handleSubmit}
              >
                <Stack gap={5}>
                  <ChildrenFields />
                  <ParentFields
                    namespace="firstParent"
                    title="First Parent"
                    required
                  />
                  <ParentFields
                    namespace="secondParent"
                    title="Second Parent"
                  />
                  <HouseholdFields />
                  <Box>
                    <Box pb={1}>
                      <Typography variant="h5">Reason for Request</Typography>
                    </Box>
                    <TextFormField
                      name="uniqueNeeds"
                      label="A summary of the child or children's history and unique needs"
                      multiline
                    />
                  </Box>
                  <ItemFieldsList />
                  <LettersOfSupport />
                  <Collection />
                  <ConsentAndAuthorization />
                </Stack>
                <Box ml={5} mt={3}>
                  <SubmitButton
                    type="submit"
                    disabled={!recaptchaReady}
                    isSubmitting={submitting}
                  />
                </Box>
              </form>
            )}
          />
        </Collapse>
      </Stack>
      <ErrorNotification open={showError} onClose={() => setShowError(false)} />
    </>
  );
}
